import { go } from 'fxjs/es';
import { createCanvasElement, makeCanvasByUrl } from '../../../Canvas/S/util.js';
import { UtilS } from '../../../Util/S/Function/module/UtilS.js';
/**
 * 이미지를 pixelated 스타일로 리사이징하는 함수
 * @param {string} image_url - 리사이징할 이미지의 URL
 * @param {number} size - 타겟 사이즈 (긴 변 기준)
 * @throws {Error} 유효하지 않은 입력값이나 이미지 크기가 타겟 사이즈보다 작을 경우
 * @returns {Promise<HTMLCanvasElement>} 리사이징된 캔버스
 */
export async function getScaleUpCanvasPixelated(image_url, size) {
  if (!image_url || typeof image_url !== 'string') {
    throw new Error('Invalid image URL: URL must be a non-empty string');
  }

  if (!Number.isInteger(size) || size <= 0) {
    throw new Error('Invalid size: size must be a positive integer');
  }

  const canvas = await makeCanvasByUrl(UtilS.ensureUrlProtocol(image_url));

  const { width: originalWidth, height: originalHeight } = canvas;
  const maxDimension = Math.max(originalWidth, originalHeight);

  if (size < originalWidth || size < originalHeight) {
    throw new Error('Target size must be larger than both original width and height');
  }

  const scale = size / maxDimension;
  const newWidth = Math.round(originalWidth * scale);
  const newHeight = Math.round(originalHeight * scale);

  const resizedCanvas = createCanvasElement({ width: newWidth, height: newHeight });

  const ctx = resizedCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(canvas, 0, 0, newWidth, newHeight);
  return resizedCanvas;
}

/**
 * 캔버스의 중앙을 기준으로 지정된 크기만큼 crop하는 함수
 * @param {HTMLCanvasElement} canvas - 원본 캔버스
 * @param {number} width - crop할 너비
 * @param {number} height - crop할 높이
 * @throws {Error} 유효하지 않은 입력값이나 crop 크기가 원본보다 큰 경우
 * @returns {HTMLCanvasElement} crop된 캔버스
 */
export function getCenterCropCanvas(canvas, width, height) {
  if (!Number.isInteger(width) || width <= 0) {
    throw new Error('Invalid width: must be a positive integer');
  }

  if (!Number.isInteger(height) || height <= 0) {
    throw new Error('Invalid height: must be a positive integer');
  }

  if (width > canvas.width || height > canvas.height) {
    throw new Error('Crop dimensions cannot be larger than original canvas');
  }

  const startX = Math.floor((canvas.width - width) / 2);
  const startY = Math.floor((canvas.height - height) / 2);

  const croppedCanvas = createCanvasElement({ width, height });

  const ctx = croppedCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  ctx.drawImage(
    canvas,
    startX,
    startY,
    width,
    height, // 소스 영역
    0,
    0,
    width,
    height, // 대상 영역
  );

  return croppedCanvas;
}

/**
 * 이미지를 pixelated 스타일로 리사이징하고 중앙 crop 하는 함수
 * @param {string} image_url - 리사이징할 이미지의 URL
 * @param {number} size - 리사이징할 타겟 사이즈 (긴 변 기준)
 * @param {number} centerCropWidth - 중심에서 crop 할 너비
 * @param {number} centerCropHeight - 중심에서 crop 할 높이
 * @throws {Error} 유효하지 않은 입력값이나 크기 제한 조건 위반 시
 * @returns {Promise<HTMLCanvasElement>} 리사이징 및 crop 된 캔버스
 */
export async function getScaleUpAndCenterCropCanvas(image_url, size, centerCropWidth, centerCropHeight) {
  // 입력값 검증
  if (!image_url || typeof image_url !== 'string') {
    throw new Error('Invalid image URL: URL must be a non-empty string');
  }

  if (!Number.isInteger(size) || size <= 0) {
    throw new Error('Invalid size: size must be a positive integer');
  }

  if (!Number.isInteger(centerCropWidth) || centerCropWidth <= 0) {
    throw new Error('Invalid cropWidth: must be a positive integer');
  }

  if (!Number.isInteger(centerCropHeight) || centerCropHeight <= 0) {
    throw new Error('Invalid cropHeight: must be a positive integer');
  }

  return go(
    image_url,
    (url) => getScaleUpCanvasPixelated(url, size),
    (canvas) => getCenterCropCanvas(canvas, centerCropWidth, centerCropHeight),
  );
}

/**
 * 캔버스 내부의 이미지를 지정된 위치로 이동시키는 함수
 * @param {HTMLCanvasElement} canvas - 원본 캔버스
 * @param {Object} position - 이동할 위치 좌표
 * @param {number} position.x - x축 이동 거리
 * @param {number} position.y - y축 이동 거리
 * @throws {Error} 유효하지 않은 입력값이나 이동 범위가 캔버스를 벗어날 경우
 * @returns {HTMLCanvasElement} 이미지가 이동된 새 캔버스
 */
export function moveDesignInCanvas(canvas, { x = 0, y = 0 }) {
  if (!Number.isInteger(x)) {
    throw new Error('Invalid x: must be an integer');
  }

  if (!Number.isInteger(y)) {
    throw new Error('Invalid y: must be an integer');
  }

  if (Math.abs(x) >= canvas.width) {
    throw new Error(`X position exceeds canvas width, canvas width: ${canvas.width}, x: ${x}`);
  }

  if (Math.abs(y) >= canvas.height) {
    throw new Error(`Y position exceeds canvas height, canvas height: ${canvas.height}, y: ${y}`);
  }

  // 동일한 크기의 새 캔버스 생성
  const movedCanvas = createCanvasElement({
    width: canvas.width,
    height: canvas.height,
  });

  const ctx = movedCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // 이미지를 새로운 위치에 그리기
  ctx.drawImage(
    canvas,
    0,
    0,
    canvas.width,
    canvas.height, // 소스 영역 (전체)
    x,
    y,
    canvas.width,
    canvas.height, // 대상 영역 (이동된 위치)
  );

  return movedCanvas;
}

export const initializeCanvases = async ({ canvas, image_url, size }) => {
  if (!canvas) return;

  try {
    const scaledCanvas = await getScaleUpCanvasPixelated(image_url || canvas.dataset.src, size);
    canvas.width = scaledCanvas.width;
    canvas.height = scaledCanvas.height;
    canvas.getContext('2d').drawImage(scaledCanvas, 0, 0);

    const avatar_bottom_y_ratio = Number(canvas.dataset.avatar_bottom_y_ratio);
    canvas.style.setProperty('--foot-position-ratio', avatar_bottom_y_ratio || 0.627);
  } catch (error) {
    console.error('Canvas initialization failed:', error);
  }
};

export function changeMapleImageUrlSize(url, size) {
  if (typeof url !== 'string' || typeof size !== 'number') {
    throw new Error('Invalid input: URL must be a string and size must be a number.');
  }

  // Use URL API to parse the URL
  const urlObj = new URL(url);

  // Update the search parameters for width and height
  urlObj.searchParams.set('width', size);
  urlObj.searchParams.set('height', size);

  // Return the updated URL
  return urlObj.toString();
}
